import React, { ChangeEvent } from 'react';
import { GeoCodeLocation, PlacePrediction } from 'services/LocationService/types';
import {
  MuiAutoComplete,
  MuiBox,
  RenderInputParams,
  MuiInputBase,
  MuiIconButton
} from 'theme/material-ui';
import { GroupBox, GroupHeader, AutoCompleteSmallBox } from 'components/AutoComplete/styled';
import SearchIcon from '@material-ui/icons/Search';
import { Svg } from 'components/UI/Svg';
import { Color } from 'modules/styles/colors';
import { IconSize } from 'modules/styles/variables';
import { SpinnerInline } from 'components/UI/Spinner/SpinnerModal';

interface Props {
  options: GeoCodeLocation[];
  handleOnPress: (val: GeoCodeLocation) => void;
  handleInputChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  hideIcon: boolean;
  placeholder?: string;
  noOptionsText?: string;
  useMyLocation: boolean;
  clearMyLocation: Function;
  isLoading: boolean;
  testId: string;
}

let textTimer: NodeJS.Timeout;

export function LocationAutoComplete({
  options,
  handleOnPress,
  handleInputChange,
  hideIcon,
  noOptionsText,
  placeholder,
  useMyLocation,
  clearMyLocation,
  isLoading,
  testId
}: Props) {
  return useMyLocation ? (
    <AutoCompleteSmallBox display="flex" width="auto">
      <MuiBox
        style={{ width: '100%', padding: '7px 15px' }}
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <span>My Location</span>
        <MuiIconButton
          onClick={clearMyLocation}
          color="primary"
          component="button"
          style={{ padding: '8px 0 8px 10px' }}
          edge="end"
        >
          <Svg color={Color.primary} set="material" name="close" size={IconSize.small} />
        </MuiIconButton>
      </MuiBox>
    </AutoCompleteSmallBox>
  ) : (
    <MuiAutoComplete
      filterOptions={options => options} // This is needed to allow google to choose what is filtered and not just on the exact string. IE: "10 west main st danville" and "10 West Main St. Danville" are the same address to google, but MuiAutoComplete will filter any devation out.
      options={options}
      noOptionsText={noOptionsText}
      getOptionLabel={(option: GeoCodeLocation) => option.description}
      autoSelect
      autoHighlight
      onChange={(evt: ChangeEvent<{}>, val: GeoCodeLocation | null): void => {
        if (val && val.description) {
          handleOnPress(val);
        }
      }}
      renderGroup={params => {
        return (
          <GroupBox key={params.key}>
            <MuiBox
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              borderBottom="1px solid rgba(151,151,151,0.15)"
            >
              <GroupHeader>{params.key}</GroupHeader>
            </MuiBox>
            {params.children}
          </GroupBox>
        );
      }}
      renderInput={(params: RenderInputParams) => (
        <AutoCompleteSmallBox display="flex" width="auto">
          <MuiInputBase
            style={{ flex: '1 1 auto', paddingRight: isLoading ? 40 : 15 }}
            ref={params.InputProps.ref}
            inputProps={{ ...params.inputProps, 'data-testid': testId }}
            placeholder={placeholder}
            onChange={(evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              const { value } = evt.target;
              clearTimeout(textTimer);
              textTimer = setTimeout(() => handleInputChange(value), 1000);
            }}
            autoComplete="off"
            name="autocompleteInput"
          />
          {!hideIcon ? <SearchIcon /> : null}
          <SpinnerInline isLoading={isLoading} position="absolute" />
        </AutoCompleteSmallBox>
      )}
    />
  );
}
